/**
 * Camada única de agregação para o hub de projetos.
 * Todas as abas devem consumir ProjectDashboardData — nunca fontes paralelas.
 */
import type { Action, CardData, Project } from '../types';
import type { EnterpriseProject } from '../types/enterpriseProject';
import type { RoadmapItem } from '../types/consultingProject';
import type { ProjectPlan, PlanMetrics, ProjectPlansSummary } from '../types/projectPlan';
import type { ProjectStage } from '../store/useProjectStore';
import type { EnterpriseAction } from './actionService';
import type {
  ProjectHubBlueprint,
  ProjectHubExecution,
  ProjectHubIssue,
} from './projectService';
import { isActionOverdue } from '../utils/actionDeadline';
import { mergeEnterpriseWithLocalActions } from '../utils/enterpriseActionMapper';
import {
  extractProjectMapSnapshot,
  projectHasModeledProcess,
  type ProjectMapSnapshot,
} from '../utils/projectMapExtraction';
import { normalizeSupabaseProjectRow, type SupabaseProjectRow } from '../utils/projectHydration';
import { normalizeActionsForProject } from './actionSyncService';

const JOURNEY_STAGES: ProjectStage[] = ['diagnosis', 'kaizen', 'planning', 'execution', 'sustainment'];

export interface ProjectDashboardData {
  project: EnterpriseProject;
  mapProject: Project;
  mapSnapshot: ProjectMapSnapshot;
  hasVsm: boolean;
  vsmCards: CardData[];
  actions: Action[];
  roadmapItems: RoadmapItem[];
  issues: ProjectHubIssue[];
  executions: ProjectHubExecution[];
  blueprints: ProjectHubBlueprint[];
  plans: ProjectPlan[];
  planMetrics: PlanMetrics[];
  summary: ProjectPlansSummary;
  counts: {
    processos: number;
    elementosVsm: number;
    acoes: number;
    problemas: number;
    marcos: number;
  };
}

export interface BuildProjectDashboardInput {
  projectRow: EnterpriseProject & Partial<SupabaseProjectRow>;
  enterpriseActions: EnterpriseAction[];
  localActions: Action[];
  roadmapItems: RoadmapItem[];
  issues: ProjectHubIssue[];
  executions: ProjectHubExecution[];
  blueprints: ProjectHubBlueprint[];
  plans: ProjectPlan[];
  planMetrics: PlanMetrics[];
}

function emptySummary(): ProjectPlansSummary {
  return {
    total_plans: 0,
    actions_overdue: 0,
    actions_total: 0,
    actions_completed: 0,
    issues_critical: 0,
    issues_total: 0,
    issues_resolved: 0,
    executions_active: 0,
    executions_total: 0,
    executions_completed: 0,
    executions_sla_risk: 0,
    progress: 0,
  };
}

function isIssueResolved(status: string): boolean {
  const normalized = status.toLowerCase();
  return normalized === 'resolved' || normalized === 'closed' || normalized === 'done';
}

function isIssueCritical(severity: string): boolean {
  const normalized = severity.toLowerCase();
  return normalized === 'critical' || normalized === 'high' || normalized === 'urgent';
}

function computeUnifiedProgress(input: {
  completedStages: string[];
  stage: string | null;
  roadmapItems: RoadmapItem[];
  actions: Action[];
}): number {
  const completedSet = new Set(input.completedStages);
  const completedCount = JOURNEY_STAGES.filter((s) => completedSet.has(s)).length;
  const currentIdx = input.stage ? JOURNEY_STAGES.indexOf(input.stage as ProjectStage) : -1;
  const currentStageBonus =
    currentIdx >= 0 && !completedSet.has(JOURNEY_STAGES[currentIdx]) ? 0.5 : 0;
  const journeyPct = ((completedCount + currentStageBonus) / JOURNEY_STAGES.length) * 100;

  const activeMilestones = input.roadmapItems.filter((item) => item.status !== 'cancelled');
  const milestonePct = activeMilestones.length > 0
    ? (activeMilestones.filter((item) => item.status === 'completed').length / activeMilestones.length) * 100
    : null;

  const activeActions = input.actions.filter((a) => a.status !== 'canceled');
  const actionPct = activeActions.length > 0
    ? (activeActions.filter((a) => a.status === 'done').length / activeActions.length) * 100
    : null;

  const weights: Array<{ value: number; weight: number }> = [{ value: journeyPct, weight: 40 }];
  if (milestonePct != null) weights.push({ value: milestonePct, weight: 30 });
  if (actionPct != null) weights.push({ value: actionPct, weight: 30 });

  const totalWeight = weights.reduce((sum, part) => sum + part.weight, 0);
  const blended = weights.reduce((sum, part) => sum + part.value * part.weight, 0) / totalWeight;
  return Math.round(Math.min(100, Math.max(0, blended)));
}

function buildSummary(input: {
  actions: Action[];
  issues: ProjectHubIssue[];
  executions: ProjectHubExecution[];
  plans: ProjectPlan[];
  planMetrics: PlanMetrics[];
  roadmapItems: RoadmapItem[];
  completedStages: string[];
  stage: string | null;
}): ProjectPlansSummary {
  const activeActions = input.actions.filter((a) => a.status !== 'canceled');
  const summary = emptySummary();

  summary.total_plans = input.plans.length;
  summary.actions_total = activeActions.length;
  summary.actions_completed = activeActions.filter((a) => a.status === 'done').length;
  summary.actions_overdue = activeActions.filter((a) => isActionOverdue(a)).length;

  summary.issues_total = input.issues.length;
  summary.issues_resolved = input.issues.filter((issue) => isIssueResolved(issue.status)).length;
  summary.issues_critical = input.issues.filter(
    (issue) => !isIssueResolved(issue.status) && isIssueCritical(issue.severity),
  ).length;

  summary.executions_total = input.executions.length;
  summary.executions_completed = input.executions.filter(
    (exec) => exec.status === 'completed' || exec.completed_at,
  ).length;
  summary.executions_active = input.executions.filter(
    (exec) => exec.status === 'active' || exec.status === 'in_progress' || exec.status === 'running',
  ).length;
  summary.executions_sla_risk = input.executions.filter(
    (exec) => exec.status === 'sla_risk' || exec.status === 'overdue',
  ).length;

  summary.progress = computeUnifiedProgress({
    completedStages: input.completedStages,
    stage: input.stage,
    roadmapItems: input.roadmapItems,
    actions: input.actions,
  });

  return summary;
}

/** Monta o objeto agregado a partir das consultas já resolvidas. */
export function buildProjectDashboardData(input: BuildProjectDashboardInput): ProjectDashboardData {
  const mapProject = normalizeSupabaseProjectRow(input.projectRow as SupabaseProjectRow);
  const mapSnapshot = extractProjectMapSnapshot(mapProject);
  const hasVsm = projectHasModeledProcess(mapSnapshot, input.blueprints.length);

  const mapEmbeddedActions = normalizeActionsForProject(
    [
      ...(mapProject.estado_atual?.actions ?? []),
      ...(mapProject.estado_futuro?.actions ?? []),
    ],
    mapProject.id,
  );
  const enterpriseMerged = mergeEnterpriseWithLocalActions(input.enterpriseActions, input.localActions);
  const actionById = new Map<string, Action>();
  for (const action of mapEmbeddedActions) actionById.set(action.id, action);
  for (const action of enterpriseMerged) actionById.set(action.id, action);
  const actions = Array.from(actionById.values());

  const milestoneCount = input.roadmapItems.filter(
    (item) => item.type === 'milestone' && item.status !== 'cancelled',
  ).length;

  const summary = buildSummary({
    actions,
    issues: input.issues,
    executions: input.executions,
    plans: input.plans,
    planMetrics: input.planMetrics,
    roadmapItems: input.roadmapItems,
    completedStages: mapSnapshot.completedStages,
    stage: mapSnapshot.stage,
  });

  return {
    project: input.projectRow,
    mapProject,
    mapSnapshot,
    hasVsm,
    vsmCards: mapSnapshot.allCards,
    actions,
    roadmapItems: input.roadmapItems,
    issues: input.issues,
    executions: input.executions,
    blueprints: input.blueprints,
    plans: input.plans,
    planMetrics: input.planMetrics,
    summary,
    counts: {
      processos: mapSnapshot.processCount,
      elementosVsm: mapSnapshot.elementCount,
      acoes: actions.length,
      problemas: input.issues.length,
      marcos: milestoneCount,
    },
  };
}

/** Log temporário para diagnóstico de divergências entre abas. */
export function logProjectDashboardCounts(data: ProjectDashboardData): void {
  console.log('[ProjectDashboard]', {
    Projeto: data.project.name ?? (data.project as { nome_projeto?: string }).nome_projeto ?? data.project.id,
    'Qtd Processos': data.counts.processos,
    'Qtd Elementos VSM': data.counts.elementosVsm,
    'Qtd Ações': data.counts.acoes,
    'Qtd Problemas': data.counts.problemas,
    'Qtd Marcos': data.counts.marcos,
  });
}

export { emptySummary as emptyProjectDashboardSummary };
